import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import { getLatestFY } from "../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import SimpleDropdown from "../../globalComponents/form/dropdowns/SimpleDropdown";
import Spinner from "../../globalComponents/ui/Spinner";
import ViewOnlyBanner from "./Components/ViewOnlyBanner";
import { parseTags, questionMatchesSteps } from "./Components/TagFilterUtils";
import TagFilterBar from "./Components/TagFilterBar";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function uid() {
  return `_${Math.random().toString(36).slice(2, 9)}`;
}

// ─── Source helpers (mirrors DocSense pattern) ────────────────────────────────
function getSourceLabel(source) {
  if (!source) return "—";
  return source.unitCode
    ? source.unitCode
    : `${source.location?.city ?? ""} - ${source.location?.state ?? ""}`;
}

// Unit label: "KMC - T1" when sub-location exists, "KMC" when standalone
function getUnitLabel(source, subLocationObj) {
  if (!source) return "—";
  const srcLabel = getSourceLabel(source);
  if (subLocationObj?.subLocation) return `${srcLabel} - ${subLocationObj.subLocation}`;
  return srcLabel;
}

// ─── Read-only asset chip row ─────────────────────────────────────────────────
function AssetReadRow({ asset, sources, onEdit, onDelete, disabled, deleting }) {
  const safeSources = sources || [];
  const sourceObj = safeSources.find(s => String(s.id) === String(asset.sourceId));
  const subObj = sourceObj?.subLocation?.find(s => String(s.id) === String(asset.subLocationId));
  const unitLabel = getUnitLabel(sourceObj, subObj);

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: "1px solid #f1f5f9", background: deleting ? "#fff5f5" : "#fff", transition: "background 0.1s" }}
      onMouseEnter={e => { if (!deleting) e.currentTarget.style.background = "#f8fafc"; }}
      onMouseLeave={e => { if (!deleting) e.currentTarget.style.background = "#fff"; }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: deleting ? "#94a3b8" : "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {asset.assetTitle}
        </div>
        <div style={{ display: "flex", gap: 5, marginTop: 3 }}>
          <span style={TAG_STYLE("#dcfce7", "#166534")}>
            <i className="fas fa-map-marker-alt" style={{ marginRight: 3, fontSize: 9 }} />
            {unitLabel}
          </span>
        </div>
      </div>
      {deleting ? (
        <Spinner size={14} color="#ef4444" />
      ) : !disabled && (
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <IconBtn icon="fa-pen" color="#3f88a5" hoverColor="#1d4ed8" title="Edit name" onClick={() => onEdit(asset)} />
          <IconBtn icon="fa-trash" color="#ef4949" hoverColor="#ec2b2b" title="Delete" onClick={() => onDelete(asset._localId, asset.id)} />
        </div>
      )}
    </div>
  );
}

// ─── Inline edit row (edit name only, unit fixed) ────────────────────────────
function AssetEditRow({ asset, sources, allAssets, onSave, onCancel }) {
  const [title, setTitle] = useState(asset.assetTitle ?? "");
  const [dupError, setDupError] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const safeSources = sources || [];
  const sourceObj = safeSources.find(s => String(s.id) === String(asset.sourceId));
  const subObj = sourceObj?.subLocation?.find(s => String(s.id) === String(asset.subLocationId));
  const unitLabel = getUnitLabel(sourceObj, subObj);

  // Titles taken by other assets on the same unit (exclude self)
  const takenTitles = useMemo(() => new Set(
    (allAssets || [])
      .filter(a =>
        a._localId !== asset._localId &&
        String(a.sourceId) === String(asset.sourceId) &&
        String(a.subLocationId ?? "") === String(asset.subLocationId ?? "")
      )
      .map(a => a.assetTitle.trim().toLowerCase())
  ), [allAssets, asset]);

  const handleSave = () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    if (takenTitles.has(trimmed.toLowerCase())) {
      setDupError(`An asset named "${trimmed}" already exists for this location.`);
      return;
    }
    onSave(trimmed);
  };

  const isValid = title.trim() && !dupError;

  return (
    <div style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9", background: "#f0f9ff" }}>
      <div style={{ fontSize: 10, color: "#0369a1", fontWeight: 600, marginBottom: 6 }}>
        <i className="fas fa-map-marker-alt" style={{ marginRight: 4 }} />{unitLabel}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ flex: 1 }}>
          <input
            ref={inputRef}
            value={title}
            onChange={e => { setTitle(e.target.value); if (dupError) setDupError(""); }}
            placeholder="Asset name…"
            style={{
              width: "100%", padding: "7px 10px", borderRadius: 7,
              border: `1.5px solid ${dupError ? "#fca5a5" : title.trim() ? "#93c5fd" : "#e2e8f0"}`,
              fontSize: 12, outline: "none", color: "#1e293b", background: "#fff", fontFamily: "inherit",
            }}
            onKeyDown={e => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") onCancel(); }}
          />
          {dupError && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, padding: "4px 8px", borderRadius: 5, background: "#fef2f2", border: "1px solid #fecaca" }}>
              <i className="fas fa-exclamation-circle" style={{ fontSize: 10, color: "#ef4444" }} />
              <span style={{ fontSize: 11, color: "#dc2626" }}>{dupError}</span>
            </div>
          )}
        </div>
        <button onClick={onCancel} style={{ ...GHOST_BTN_STYLE, alignSelf: "flex-start" }}>Cancel</button>
        <button
          onClick={handleSave}
          disabled={!isValid}
          style={{ ...ACTION_BTN_STYLE, alignSelf: "flex-start", background: isValid ? "#3f88a5" : "#e2e8f0", color: isValid ? "#fff" : "#94a3b8", cursor: isValid ? "pointer" : "not-allowed" }}
        >
          <i className="fas fa-check" style={{ marginRight: 4 }} />Save
        </button>
      </div>
    </div>
  );
}

// ─── Add-asset panel: pick unit once, tag multiple names, save directly ──────
function AddAssetPanel({ kpiId, latestFYId, sources, fyOptions, existingAssets, onSaved, onClose }) {
  const safeSources = sources || [];
  const [sourceId, setSourceId] = useState(null);
  const [subLocationId, setSubLocationId] = useState(null);
  const [fyid, setFyid] = useState(latestFYId ?? null);
  const [nameInput, setNameInput] = useState("");
  const [pendingNames, setPendingNames] = useState([]);
  const [dupError, setDupError] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const subLocationOptions = useMemo(() => {
    if (!sourceId) return [];
    return safeSources.find(s => String(s.id) === String(sourceId))?.subLocation || [];
  }, [safeSources, sourceId]);

  const selectedSourceHasSubs = subLocationOptions.length > 0;
  const unitReady = sourceId && (!selectedSourceHasSubs || subLocationId);

  const sourceObj = safeSources.find(s => String(s.id) === String(sourceId));
  const subObj = subLocationOptions.find(s => String(s.id) === String(subLocationId));
  const unitLabel = unitReady ? getUnitLabel(sourceObj, subObj) : null;

  // Titles already in use for the selected unit
  const existingTitlesForUnit = useMemo(() => {
    if (!sourceId) return new Set();
    return new Set(
      (existingAssets || [])
        .filter(a =>
          String(a.sourceId) === String(sourceId) &&
          String(a.subLocationId ?? "") === String(subLocationId ?? "")
        )
        .map(a => a.assetTitle.trim().toLowerCase())
    );
  }, [existingAssets, sourceId, subLocationId]);

  // Clear staged names when unit changes
  useEffect(() => {
    setPendingNames([]);
    setDupError("");
    setNameInput("");
  }, [sourceId, subLocationId]);

  const addName = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    if (pendingNames.map(n => n.toLowerCase()).includes(trimmed.toLowerCase())) {
      setDupError(`"${trimmed}" is already in the list above.`);
      return;
    }
    if (existingTitlesForUnit.has(trimmed.toLowerCase())) {
      setDupError(`An asset named "${trimmed}" already exists for this location.`);
      return;
    }
    setDupError("");
    setPendingNames(prev => [...prev, trimmed]);
    setNameInput("");
    inputRef.current?.focus();
  };

  const removeName = (name) => { setPendingNames(prev => prev.filter(n => n !== name)); setDupError(""); };

  const handleSave = async () => {
    const inputTrimmed = nameInput.trim();
    let all = [...pendingNames];

    if (inputTrimmed) {
      if (
        all.map(n => n.toLowerCase()).includes(inputTrimmed.toLowerCase()) ||
        existingTitlesForUnit.has(inputTrimmed.toLowerCase())
      ) {
        setDupError(`An asset named "${inputTrimmed}" already exists for this location.`);
        return;
      }
      all = [...all, inputTrimmed];
    }

    if (!all.length || !fyid || !sourceId) return;

    setSaving(true);
    try {
      const mappings = all.map(assetTitle => ({
        kpiId: Number(kpiId),
        sourceId: Number(sourceId),
        subLocationId: subLocationId ? Number(subLocationId) : undefined,
        assetTitle,
      }));

      await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}kpiAssetSettings`,
        {},
        { fyid: Number(fyid), mappings },
        "POST"
      );

      onSaved();  // triggers fetchAll in parent
      onClose();
    } catch (e) {
      console.error(e);
      setDupError("Save failed — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const canSave = !saving && fyid && unitReady && (pendingNames.length > 0 || nameInput.trim());
  const totalToSave = pendingNames.length + (nameInput.trim() ? 1 : 0);

  return (
    <div style={{ margin: "0 16px 12px", background: "#fff", border: "1.5px solid #bae6fd", borderRadius: 10, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "8px 14px", background: "#f0f9ff", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: "#0369a1", textTransform: "uppercase", letterSpacing: "0.5px" }}>Add Assets</span>
        <button onClick={onClose} style={{ fontSize: 12, border: "none", background: "none", color: "#94a3b8", cursor: "pointer", fontWeight: 700, padding: "0 2px" }}
          onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
          onMouseLeave={e => e.currentTarget.style.color = "#94a3b8"}
        >✕</button>
      </div>

      <div style={{ padding: "12px 14px" }}>
        {/* FY + Location + Sub-location */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
          <div>
            <label style={LABEL_STYLE}>Financial Year *</label>
            <select value={fyid ?? ""} onChange={e => { const v = e.target.value; setFyid(v ? Number(v) : null); }} style={SELECT_STYLE(!!fyid)}>
              <option value="">Select FY…</option>
              {(fyOptions || []).map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label style={LABEL_STYLE}>Location *</label>
            <select value={sourceId ?? ""} onChange={e => { const v = e.target.value; setSourceId(v ? Number(v) : null); setSubLocationId(null); }} style={SELECT_STYLE(!!sourceId)}>
              <option value="">Select location…</option>
              {safeSources.map(s => <option key={s.id} value={s.id}>{getSourceLabel(s)}</option>)}
            </select>
          </div>
          <div>
            <label style={LABEL_STYLE}>Sub-location {selectedSourceHasSubs ? "*" : ""}</label>
            <select value={subLocationId ?? ""} onChange={e => { const v = e.target.value; setSubLocationId(v ? Number(v) : null); }} style={SELECT_STYLE(!!subLocationId)} disabled={!sourceId || subLocationOptions.length === 0}>
              <option value="">{!sourceId ? "Select location first" : subLocationOptions.length === 0 ? "No sub-locations" : "Select sub-location…"}</option>
              {subLocationOptions.map(s => <option key={s.id} value={s.id}>{s.subLocation}</option>)}
            </select>
          </div>
        </div>

        {/* Unit pill */}
        {unitLabel && (
          <div style={{ marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, background: "#dcfce7", border: "1px solid #86efac" }}>
            <i className="fas fa-map-marker-alt" style={{ fontSize: 9, color: "#166534" }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: "#166534" }}>{unitLabel}</span>
          </div>
        )}

        {/* Staged name tags */}
        {pendingNames.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {pendingNames.map(name => (
              <span key={name} style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, background: "#e0f2fe", border: "1px solid #7dd3fc", fontSize: 12, fontWeight: 600, color: "#0369a1" }}>
                {name}
                <span onClick={() => removeName(name)} style={{ cursor: "pointer", color: "#93c5fd", fontWeight: 900, fontSize: 13, lineHeight: 1 }}
                  onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                  onMouseLeave={e => e.currentTarget.style.color = "#93c5fd"}
                >×</span>
              </span>
            ))}
          </div>
        )}

        {/* Name input */}
        <div>
          <label style={LABEL_STYLE}>
            Asset Names * <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— press Enter or + to stage, then Save Assets</span>
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, position: "relative" }}>
              <input
                ref={inputRef}
                value={nameInput}
                onChange={e => { setNameInput(e.target.value); if (dupError) setDupError(""); }}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addName(); } }}
                placeholder={unitReady ? "e.g. DG Stack Bangalore 01" : "Select location first…"}
                disabled={!unitReady || saving}
                style={{
                  width: "100%", padding: "7px 10px", borderRadius: 7,
                  border: `1.5px solid ${dupError ? "#fca5a5" : nameInput.trim() ? "#93c5fd" : "#e2e8f0"}`,
                  fontSize: 12, outline: "none", color: "#1e293b",
                  background: unitReady ? "#fff" : "#f8fafc", fontFamily: "inherit",
                }}
              />
            </div>
            <button onClick={addName} disabled={!nameInput.trim() || !unitReady || saving}
              style={{ ...ACTION_BTN_STYLE, padding: "7px 12px", background: nameInput.trim() && unitReady ? "#e0f2fe" : "#f1f5f9", color: nameInput.trim() && unitReady ? "#0369a1" : "#cbd5e1", border: "1.5px solid transparent", cursor: nameInput.trim() && unitReady ? "pointer" : "not-allowed" }}
            >+ Add</button>
          </div>

          {dupError && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, padding: "5px 10px", borderRadius: 6, background: "#fef2f2", border: "1px solid #fecaca" }}>
              <i className="fas fa-exclamation-circle" style={{ fontSize: 11, color: "#ef4444", flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: "#dc2626", fontWeight: 500 }}>{dupError}</span>
            </div>
          )}
        </div>

        {/* Save Assets button */}
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={handleSave} disabled={!canSave}
            style={{ ...ACTION_BTN_STYLE, background: canSave ? "#3f88a5" : "#e2e8f0", color: canSave ? "#fff" : "#94a3b8", cursor: canSave ? "pointer" : "not-allowed" }}
          >
            {saving
              ? <><Spinner size={11} color="#fff" style={{ marginRight: 6 }} />Saving…</>
              : <><i className="fas fa-save" style={{ marginRight: 5 }} />Save {totalToSave > 0 ? `${totalToSave} ` : ""}Asset{totalToSave !== 1 ? "s" : ""}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Row with expandable asset editor ────────────────────────────────────
function KpiAssetRow({ kpi, assets, sources, fyOptions, latestFYId, onSaved, onError, disabled, isLast }) {
  const [open, setOpen] = useState(false);
  const [localAssets, setLocalAssets] = useState(assets);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Sync from parent on genuine server refresh (stable id fingerprint)
  const assetsJsonRef = useRef(null);
  useEffect(() => {
    const next = JSON.stringify(assets.map(a => a.id));
    if (next !== assetsJsonRef.current) {
      assetsJsonRef.current = next;
      setLocalAssets(assets);
      setShowAddPanel(false);
      setEditingAsset(null);
    }
  }, [assets]);

  // Delete fires immediately against the API
  const deleteAsset = async (localId, serverId) => {
    if (!serverId) {
      setLocalAssets(prev => prev.filter(a => a._localId !== localId));
      return;
    }
    setDeletingId(serverId);
    try {
      await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}kpiAssetSettings/${serverId}`, {}, {}, "DELETE");
      onSaved();
    } catch (e) {
      console.error(e);
      onError("Failed to delete asset — please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // Rename fires immediately via PATCH
  const handleEditSave = async (localId, serverId, newTitle) => {
    if (!serverId) return;
    try {
      await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}kpiAssetSettings/${serverId}`, {}, { assetTitle: newTitle }, "PATCH");
      onSaved();
      setEditingAsset(null);
    } catch (e) {
      console.error(e);
      onError("Failed to rename asset — please try again.");
    }
  };

  const assetCount = localAssets.length;

  return (
    <div style={{ borderBottom: isLast ? "none" : "1px solid #f1f5f9" }}>
      {/* Header row */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 16px", cursor: "pointer", background: open ? "#f8fafc" : "#fff", transition: "background 0.15s", userSelect: "none" }}
      >
        <div style={{ width: 22, height: 22, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", background: open ? "#3f88a5" : "#e2e8f0", transition: "all 0.2s", flexShrink: 0 }}>
          <span style={{ fontSize: 8, color: open ? "#fff" : "#64748b", transform: open ? "rotate(90deg)" : "none", display: "block", transition: "transform 0.2s" }}>▶</span>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={kpi.title}>
            {kpi.title || "—"}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {assetCount > 0 ? (
            <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd" }}>
              <i className="fas fa-industry" style={{ marginRight: 4, fontSize: 9 }} />
              {assetCount} asset{assetCount !== 1 ? "s" : ""}
            </span>
          ) : (
            <span style={{ fontSize: 10, color: "#cbd5e1", fontStyle: "italic" }}>no assets</span>
          )}
          {!disabled && (
            <button
              onClick={e => { e.stopPropagation(); setOpen(true); setShowAddPanel(true); }}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, border: "1.5px solid #bae6fd", background: "#f0f9ff", fontSize: 11, fontWeight: 600, color: "#0369a1", cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = "#dbeafe"}
              onMouseLeave={e => e.currentTarget.style.background = "#f0f9ff"}
            >
              <i className="fas fa-plus" style={{ fontSize: 9 }} /> Add
            </button>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {open && (
        <div style={{ background: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
          {/* Add panel */}
          {showAddPanel && !disabled && (
            <AddAssetPanel
              kpiId={kpi.questionId}
              latestFYId={latestFYId}
              sources={sources}
              fyOptions={fyOptions}
              existingAssets={localAssets}
              onSaved={onSaved}
              onClose={() => setShowAddPanel(false)}
            />
          )}

          {/* Asset list */}
          {localAssets.length === 0 && !showAddPanel ? (
            <div style={{ padding: "24px", textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.3 }}>🏭</div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>No assets for this KPI yet.</div>
              {!disabled && (
                <button onClick={() => setShowAddPanel(true)} style={{ ...ACTION_BTN_STYLE, background: "#3f88a5", color: "#fff", cursor: "pointer" }}>
                  <i className="fas fa-plus" style={{ marginRight: 6 }} />Add first asset
                </button>
              )}
            </div>
          ) : localAssets.length > 0 && (
            <div style={{ margin: "0 16px 12px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "7px 14px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>KPI Assets</span>
                <span style={{ fontSize: 10, color: "#94a3b8" }}>{localAssets.length} item{localAssets.length !== 1 ? "s" : ""}</span>
              </div>
              {localAssets.map(asset => (
                editingAsset?._localId === asset._localId ? (
                  <AssetEditRow
                    key={asset._localId}
                    asset={asset}
                    sources={sources}
                    allAssets={localAssets}
                    onSave={newTitle => handleEditSave(asset._localId, asset.id, newTitle)}
                    onCancel={() => setEditingAsset(null)}
                  />
                ) : (
                  <AssetReadRow
                    key={asset._localId}
                    asset={asset}
                    sources={sources}
                    onEdit={a => setEditingAsset(a)}
                    onDelete={(localId, serverId) => deleteAsset(localId, serverId)}
                    disabled={disabled || deletingId === asset.id}
                    deleting={deletingId === asset.id}
                  />
                )
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Summary legend ───────────────────────────────────────────────────────────
function AssetLegend({ kpiAssetMap, allQuestions }) {
  const withAssets = useMemo(() =>
    allQuestions.filter(q => (kpiAssetMap[q.questionId] || []).filter(a => a.id && !a._new).length > 0).length,
    [kpiAssetMap, allQuestions]
  );
  const totalAssets = useMemo(() =>
    Object.values(kpiAssetMap).flat().filter(a => a.id && !a._new).length,
    [kpiAssetMap]
  );
  const pendingAssets = useMemo(() =>
    Object.values(kpiAssetMap).flat().filter(a => a._new).length,
    [kpiAssetMap]
  );

  if (withAssets === 0 && pendingAssets === 0) return null;

  return (
    <div style={{
      background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10,
      padding: "10px 16px", marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
    }}>
      <span style={{ fontSize: 10, fontWeight: 800, color: "#0369a1", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        🏭 Asset Coverage:
      </span>
      <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#fff", border: "1px solid #bae6fd", color: "#0369a1" }}>
        {withAssets} KPI{withAssets !== 1 ? "s" : ""} have assets
      </span>
      <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#fff", border: "1px solid #bae6fd", color: "#0369a1" }}>
        {totalAssets} total asset{totalAssets !== 1 ? "s" : ""}
      </span>
      {pendingAssets > 0 && (
        <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#fef3c7", border: "1px solid #fcd34d", color: "#d97706" }}>
          {pendingAssets} unsaved
        </span>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function KPIAssetSettings() {
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [filterSteps, setFilterSteps] = useState([]);
  const [search, setSearch] = useState("");
  const [kpiView, setKpiView] = useState("list");
  const [kpiAssetMap, setKpiAssetMap] = useState({});
  const [sources, setSources] = useState([]);
  const [deletePendingMap, setDeletePendingMap] = useState({});

  const fyData = useMemo(() => { try { return JSON.parse(localStorage.getItem("financialYears")) ?? []; } catch { return []; } }, []);
  const latestFY = useMemo(() => getLatestFY(fyData), [fyData]);
  const [selectedFY, setSelectedFY] = useState(() => latestFY);
  const isViewOnly = selectedFY?.id !== latestFY?.id;

  const fyOptions = useMemo(() =>
    fyData.map(fy => ({ value: fy.id, label: `${fy.financial_year_value}${fy.id === latestFY?.id ? " (Current)" : ""}` })),
    [fyData, latestFY]
  );

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchAll = useCallback(async () => {
    if (!selectedFY?.id) return;
    setLoading(true);
    try {
      const [locRes, mappingsRes, emissionSettingsRes] = await Promise.all([
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getSource`, {}, {}, "GET"),
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}kpiAssetSettings`, {}, { fyid: selectedFY.id }, "GET"),
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {}, { fyid: selectedFY.id }, "GET"),
      ]);

      // sources shape: [{ id, unitCode, location: { city, state }, subLocation: [{ id, subLocation }] }]
      const rawSources = locRes?.data?.data || [];
      setSources(rawSources);

      // Derive the allowed KPI ID set from emissionKpiSettings
      const emissionRaw = emissionSettingsRes?.data;
      const allowedKpiIds = Array.isArray(emissionRaw?.kpiIds)
        ? new Set(emissionRaw.kpiIds.map(Number))
        : null; // null = no saved settings yet, fall back to kpi_type filter below

      const userId = JSON.parse(localStorage.getItem("user_temp_id"));
      const fwRes = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getFramework`, {}, { type: "ALL", userId }, "GET");
      const frameworks = fwRes.data?.data || [];
      const qRes = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionnew`, {},
        { financialYearId: selectedFY.id, frameworkIds: frameworks.map(f => f.id), fromSettings: false },
        "GET"
      );
      const allFetched = qRes.data?.data || [];

      // If emissionKpiSettings has an explicit saved list, use it;
      // otherwise fall back to kpi_type EMISSION filter (first-time / no settings saved yet)
      const questions = allowedKpiIds
        ? allFetched.filter(q => allowedKpiIds.has(Number(q.questionId)))
        : allFetched.filter(q => q.kpi_type?.includes("EMISSION"));

      setAllQuestions(questions);

      // Build kpiAssetMap from server data
      const serverMappings = mappingsRes?.data?.data || [];
      const map = {};
      for (const q of questions) map[q.questionId] = [];
      for (const m of serverMappings) {
        const kpiId = m.kpiId ?? m.kpi_id;
        if (!map[kpiId]) map[kpiId] = [];
        map[kpiId].push({ ...m, _localId: uid(), _new: false });
      }
      setKpiAssetMap(map);
      setDeletePendingMap({});
    } catch (e) {
      console.error(e);
      showToast("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedFY?.id]);

  useEffect(() => {
    setFilterSteps([]);
    setSearch("");
    fetchAll();
  }, [fetchAll]);

  const handleSaved = useCallback(() => {
    showToast("Saved!");
    fetchAll();
  }, [fetchAll]);

  const handleError = useCallback((msg) => {
    showToast(msg, "error");
  }, []);

  const filteredQuestions = useMemo(() => {
    let qs = allQuestions.filter(q => questionMatchesSteps(q, filterSteps));
    if (search.trim()) {
      const s = search.toLowerCase();
      qs = qs.filter(q => (q.title || "").toLowerCase().includes(s) || (q.moduleName || "").toLowerCase().includes(s));
    }
    return qs;
  }, [allQuestions, filterSteps, search]);

  const kpisWithAssets = useMemo(() =>
    allQuestions.filter(q => (kpiAssetMap[q.questionId] || []).filter(a => a.id && !a._new).length > 0),
    [allQuestions, kpiAssetMap]
  );

  const totalSaved = useMemo(() =>
    Object.values(kpiAssetMap).flat().filter(a => a.id && !a._new).length,
    [kpiAssetMap]
  );

  if (loading) {
    return (
      <div style={{ padding: 100, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <Spinner size={32} color="#3f88a5" />
        <span style={{ fontSize: 13, color: "#94a3b8" }}>Loading KPI Assets…</span>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#f1f5f9", minHeight: "100vh" }}>
      <style>{`
        @keyframes kasset-toast-in { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: translateY(0) } }
        * { box-sizing: border-box; }
        select:focus { outline: none; }
        button:focus-visible { outline: 2px solid #3f88a5; outline-offset: 2px; }
      `}</style>

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 1000,
          background: toast.type === "error" ? "#dc2626" : "#16a34a",
          color: "#fff", padding: "11px 18px", borderRadius: 10,
          fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          animation: "kasset-toast-in 0.25s ease", display: "flex", alignItems: "center", gap: 8,
        }}>
          <span>{toast.type === "error" ? "✕" : "✓"}</span>{toast.msg}
        </div>
      )}

      {/* Top Bar */}
      <div style={{
        background: "#fff", padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 8, height: 28, borderRadius: 4, background: "linear-gradient(to bottom, #3f88a5, #2563eb)" }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>KPI Asset Settings</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {kpisWithAssets.length} KPI{kpisWithAssets.length !== 1 ? "s" : ""} with assets
              {totalSaved > 0 && <span style={{ color: "#0369a1" }}> · {totalSaved} total assets</span>}
              {isViewOnly && <span style={{ color: "#b45309", fontWeight: 600 }}> · view only</span>}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {fyData.length > 0 && (
            <SimpleDropdown
              label="Financial Year"
              value={selectedFY?.id ?? ""}
              onChange={val => { const found = fyData.find(f => String(f.id) === String(val)); if (found) setSelectedFY(found); }}
              options={fyOptions}
              isAlert={selectedFY?.id !== latestFY?.id}
            />
          )}
        </div>
      </div>

      <div style={{ padding: "24px", maxWidth: 1040, margin: "0 auto" }}>

        {isViewOnly && <ViewOnlyBanner fyValue={selectedFY?.financial_year_value} />}

        <AssetLegend kpiAssetMap={kpiAssetMap} allQuestions={allQuestions} />

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, background: "#e2e8f0", padding: 3, borderRadius: 10, width: "fit-content", marginBottom: 20 }}>
          {[
            { key: "list", label: "All KPIs", count: allQuestions.length },
            { key: "with-assets", label: "With Assets", count: kpisWithAssets.length },
          ].map(tab => (
            <button key={tab.key} onClick={() => setKpiView(tab.key)} style={{
              padding: "7px 20px", border: "none", borderRadius: 8,
              background: kpiView === tab.key ? "#fff" : "transparent",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              color: kpiView === tab.key ? "#0f172a" : "#64748b",
              display: "flex", alignItems: "center", gap: 7,
              boxShadow: kpiView === tab.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              transition: "all 0.15s",
            }}>
              {tab.label}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                background: kpiView === tab.key ? (tab.key === "with-assets" ? "#dbeafe" : "#f1f5f9") : "#d1d5db",
                color: kpiView === tab.key ? (tab.key === "with-assets" ? "#1d4ed8" : "#475569") : "#94a3b8",
              }}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Filter + Search bar (All KPIs tab) */}
        {kpiView === "list" && (
          <div style={{ background: "#fff", padding: "16px", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <div style={{ position: "relative", marginBottom: 12 }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94a3b8" }}>⌕</span>
              <input
                value={search}
                onChange={e => !isViewOnly && setSearch(e.target.value)}
                readOnly={isViewOnly}
                placeholder="Search by title or module…"
                style={{
                  width: "100%", padding: "9px 12px 9px 32px", border: "1.5px solid #e2e8f0",
                  borderRadius: 9, fontSize: 13, outline: "none", color: "#1e293b",
                  background: isViewOnly ? "#f8fafc" : "#fff",
                  cursor: isViewOnly ? "default" : "text", fontFamily: "inherit",
                }}
                onFocus={e => { if (!isViewOnly) e.target.style.borderColor = "#93c5fd"; }}
                onBlur={e => e.target.style.borderColor = "#e2e8f0"}
              />
            </div>
            <TagFilterBar
              allQuestions={allQuestions}
              filterSteps={filterSteps}
              onFilterStepsChange={setFilterSteps}
              pendingIds={new Set(allQuestions.map(q => q.questionId))}
              disabled={isViewOnly}
            />
          </div>
        )}

        {/* KPI list */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          {(() => {
            const displayList = kpiView === "with-assets" ? kpisWithAssets : filteredQuestions;

            if (displayList.length === 0) {
              return (
                <div style={{ padding: "60px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.3 }}>🏭</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                    {kpiView === "with-assets" ? "No KPIs have assets yet" : "No KPIs match this filter"}
                  </div>
                  <div style={{ fontSize: 12, color: "#cbd5e1" }}>
                    {kpiView === "with-assets"
                      ? "Switch to All KPIs, expand a KPI and click Add to create assets."
                      : "Try adjusting your search or filter."}
                  </div>
                </div>
              );
            }

            return (
              <>
                <div style={{ padding: "8px 16px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>KPI Title</span>
                  <span style={{ fontSize: 10, color: "#94a3b8" }}>{displayList.length} KPI{displayList.length !== 1 ? "s" : ""}</span>
                </div>
                <div style={{ maxHeight: 620, overflowY: "auto" }}>
                  {displayList.map((q, idx) => (
                    <KpiAssetRow
                      key={q.questionId}
                      kpi={q}
                      assets={kpiAssetMap[q.questionId] || []}
                      sources={sources}
                      fyOptions={fyOptions}
                      latestFYId={latestFY?.id}
                      onSaved={handleSaved}
                      onError={handleError}
                      disabled={isViewOnly}
                      isLast={idx === displayList.length - 1}
                    />
                  ))}
                </div>
              </>
            );
          })()}
        </div>

        {/* Legend */}
        <div style={{ marginTop: 12, padding: "8px 14px", background: "#fff", border: "1px solid #f1f5f9", borderRadius: 8, display: "flex", gap: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700 }}>LEGEND:</span>
          <span style={{ fontSize: 10, color: "#0369a1" }}>🏭 = assets created per KPI (e.g. DG Stack Bangalore 01)</span>
          <span style={{ fontSize: 10, color: "#d97706" }}>Each asset requires: FY + Location + Asset Name</span>
          <span style={{ fontSize: 10, color: "#94a3b8" }}>Sub-location required when location has sub-locations (sub-location = the unit)</span>
        </div>
      </div>
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────
function TAG_STYLE(bg, color) {
  return { fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: bg, color, border: `1px solid ${color}22` };
}

const LABEL_STYLE = { fontSize: 10, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.4px" };

function SELECT_STYLE(hasValue) {
  return {
    width: "100%", padding: "7px 10px", borderRadius: 7,
    border: `1.5px solid ${hasValue ? "#93c5fd" : "#e2e8f0"}`,
    fontSize: 12, outline: "none", background: hasValue ? "#f0f9ff" : "#fff",
    color: hasValue ? "#0369a1" : "#374151", cursor: "pointer", fontFamily: "inherit",
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center", backgroundSize: "7px",
    paddingRight: 24,
  };
}

const ACTION_BTN_STYLE = {
  display: "flex", alignItems: "center", padding: "6px 14px",
  borderRadius: 7, border: "none", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
};

const GHOST_BTN_STYLE = {
  display: "flex", alignItems: "center", padding: "6px 14px",
  borderRadius: 7, border: "1.5px solid #e2e8f0", background: "#fff",
  fontSize: 12, fontWeight: 600, color: "#64748b", cursor: "pointer", fontFamily: "inherit",
};

function IconBtn({ icon, color, hoverColor, title, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      title={title}
      onClick={e => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 28, height: 28, borderRadius: 6, border: "1.5px solid #e2e8f0",
        background: hovered ? "#f8fafc" : "#fff", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: hovered ? hoverColor : color, transition: "all 0.15s",
      }}
    >
      <i className={`fas ${icon}`} style={{ fontSize: 11 }} />
    </button>
  );
}